import * as React from "react";
import { Outlet } from "react-router-dom";

import { Sidebar } from "./Sidebar";
import { TopBar } from "./TopBar";
import { SupportWidget } from "@/components/support/SupportWidget";
import { useAccount } from "@/hooks/useAccount";
import {
  Dialog,
  DialogContent,
  DialogTitle
} from "@/components/ui/dialog";

/**
 * Layout shell for every protected page. Two viewports:
 *
 *   ≥ lg (1024px): permanent left sidebar + content column on the right.
 *   < lg:         sidebar lives in a Dialog (drawer-flavored) opened
 *                 from the TopBar hamburger.
 */
export function AppShell() {
  const [mobileNavOpen, setMobileNavOpen] = React.useState(false);
  const { data: account } = useAccount();
  const isAdmin = account?.user_role === "Admin";

  return (
    <div className="flex min-h-[100dvh] flex-col bg-muted/50">
      <div className="flex flex-1">
        {/* ── Desktop sidebar — dark agency-style rail ── */}
        <aside
          className="hidden w-64 shrink-0 bg-[hsl(var(--sidebar))] lg:flex lg:flex-col"
          aria-label="Sidebar"
        >
          <Sidebar />
        </aside>

        {/* ── Main column ── */}
        <div className="flex min-w-0 flex-1 flex-col">
          <TopBar onOpenMobileNav={() => setMobileNavOpen(true)} />

          <main className="flex-1">
            <div className="mx-auto w-full max-w-[1240px] px-4 py-5 sm:px-6 sm:py-8">
              <Outlet />
            </div>
          </main>
        </div>
      </div>

      {/* ── Mobile drawer ── */}
      <Dialog open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
        <DialogContent
          className="left-0 top-0 h-[100dvh] w-[min(280px,80vw)] max-w-none translate-x-0 translate-y-0 rounded-none border-0 bg-[hsl(var(--sidebar))] p-0 sm:rounded-none"
        >
          <DialogTitle className="sr-only">Navigation</DialogTitle>
          <Sidebar onNavigate={() => setMobileNavOpen(false)} />
        </DialogContent>
      </Dialog>

      {/* Customer support widget — hidden in the operator (admin) view. */}
      {!isAdmin && <SupportWidget />}
    </div>
  );
}
